import { supabase } from '@/infrastructure/supabase/client'

const MAX_PX  = 2048
const QUALITY = 0.82

async function optimizeImage(file: File): Promise<Blob> {
  if (file.type === 'image/svg+xml' || file.type === 'image/gif') return file
  if (file.type.startsWith('video/')) return file

  return new Promise((resolve, reject) => {
    const img = new Image()
    const url = URL.createObjectURL(file)
    img.onload = () => {
      URL.revokeObjectURL(url)
      let { width, height } = img
      if (width > MAX_PX || height > MAX_PX) {
        if (width >= height) { height = Math.round(height * MAX_PX / width); width = MAX_PX }
        else                 { width = Math.round(width * MAX_PX / height); height = MAX_PX }
      }
      const canvas = document.createElement('canvas')
      canvas.width  = width
      canvas.height = height
      canvas.getContext('2d')!.drawImage(img, 0, 0, width, height)
      canvas.toBlob(blob => blob ? resolve(blob) : reject(new Error('Canvas toBlob failed')), 'image/webp', QUALITY)
    }
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('Image load failed')) }
    img.src = url
  })
}

// ── Port ──────────────────────────────────────────────────────
interface IStorageAdapter {
  upload(blob: Blob, path: string, contentType: string): Promise<string>
}

// ── Adapter A: Supabase Storage (default) ─────────────────────
class SupabaseStorageAdapter implements IStorageAdapter {
  async upload(blob: Blob, path: string, contentType: string): Promise<string> {
    const { error } = await supabase.storage.from('media').upload(path, blob, {
      contentType,
      cacheControl: '31536000',
      upsert: false,
    })
    if (error) throw new Error(`Upload failed: ${error.message}`)
    const { data } = supabase.storage.from('media').getPublicUrl(path)
    return data.publicUrl
  }
}

// ── Adapter B: Presigned URL — R2 / S3 / GCS / cualquier S3-compatible ──
// El browser pide una signed PUT URL a la Edge Function `storage-presign`,
// luego sube directo al provider. Para agregar un nuevo provider basta con
// añadir su case en storage-presign/index.ts — este adapter nunca cambia.
class PresignedUrlAdapter implements IStorageAdapter {
  async upload(blob: Blob, path: string, contentType: string): Promise<string> {
    const { data: { session } } = await supabase.auth.getSession()
    const token = session?.access_token ?? ''

    const res = await fetch(
      `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/storage-presign`,
      {
        method: 'POST',
        headers: {
          'Content-Type':  'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ path, contentType }),
      },
    )
    if (!res.ok) throw new Error(`Presign failed: ${await res.text()}`)
    const { uploadUrl, publicUrl } = await res.json() as { uploadUrl: string; publicUrl: string }

    const upload = await fetch(uploadUrl, {
      method: 'PUT',
      headers: { 'Content-Type': contentType },
      body: blob,
    })
    if (!upload.ok) throw new Error(`Storage upload failed: ${upload.status}`)
    return publicUrl
  }
}

// ── Factory ───────────────────────────────────────────────────
// VITE_STORAGE_BACKEND: "supabase" (default) | "r2" | "s3" | "gcs" | cualquier S3-compatible
// Para agregar un provider: setear VITE_STORAGE_BACKEND + añadir su case en storage-presign.
function createStorageAdapter(): IStorageAdapter {
  const backend = (import.meta.env.VITE_STORAGE_BACKEND as string | undefined) ?? 'supabase'
  if (backend === 'supabase') return new SupabaseStorageAdapter()
  return new PresignedUrlAdapter()
}

const adapter = createStorageAdapter()

// ── Public API (sin cambios para el resto del código) ─────────
export async function uploadMedia(file: File, folder = 'uploads'): Promise<string> {
  const isImage    = file.type.startsWith('image/') && file.type !== 'image/svg+xml' && file.type !== 'image/gif'
  const blob       = isImage ? await optimizeImage(file) : file
  const ext        = isImage ? 'webp' : (file.name.split('.').pop() ?? 'bin')
  const path       = `${folder}/${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`
  const contentType = isImage ? 'image/webp' : file.type
  return adapter.upload(blob, path, contentType)
}
